import NextLink from 'next/link';
import StatusCode from 'status-code-enum';
import { useEffect } from 'react';

import { savedSearchApi } from '../../../../../api/savedSearches';

import { useUserContext } from '../../../../../contexts/UserContext';

import { Link } from '../../../../../components/Toolkit/Button/Link';

import { useOnUpdateOnly } from '../../../../../hooks/UseOnUpdateOnly';
import { useAuthOptions } from '../../../../../hooks/useAuthOptions';
import { useBrowserStorage } from '../../../../../hooks/UseBrowserStorage';

import { USER_MENU } from '../../../../../components/Layouts/components/UserMenu';

import { fireToast } from '../../../../../helpers/Toasts';
import { formatAuthorizationHeader } from '../../../../../helpers/auth';

enum NotificationFrequency {
  DAILY = 'DAILY',
  INSTANT = 'INSTANT',
  OFF = 'OFF',
}

type UseSavedSearchPanelArgs = { adId: number };

const useSavedSearchPanel = (args: UseSavedSearchPanelArgs) => {
  const { adId } = args;

  const {
    user,
    setIsSavedSearchModalOpen,
    setIsInstantAlertAvailable,
    isSearchSaved,
    setIsSearchSaved,
    setSelectedNotificationFrequency,
  } = useUserContext();
  const { accessToken, status, handleLogin } = useAuthOptions();

  const authHeaders = formatAuthorizationHeader(accessToken);
  const userId = user?.id;

  const savedSearchPageHref = `/dashboard/${USER_MENU.SEARCHES}`;

  const modelSessionStorageItem = 'ad-details-saved-search-modal';

  const {
    getSessionStorageItem,
    setSessionStorageItem,
    removeSessionStorageItem,
  } = useBrowserStorage();

  const onOpen = async () => {
    if (status === 'authenticated') {
      if (userId) {
        try {
          const {
            data: { instantAlert },
          } = await savedSearchApi.adDetailsPagePreCheck(
            adId,
            userId,
            authHeaders,
          );
          if (instantAlert) {
            setIsInstantAlertAvailable(true);
            setSelectedNotificationFrequency(NotificationFrequency.INSTANT);
          } else {
            setIsInstantAlertAvailable(false);
          }
          setIsSavedSearchModalOpen(true);
        } catch (error) {
          if (
            error?.response?.status ===
            StatusCode.ClientErrorUnprocessableEntity
          ) {
            setIsSearchSaved(true);
            fireToast({
              type: 'ERROR',
              text: (
                <>
                  You already have a Saved Search for these filters.&nbsp;
                  <Link
                    ofType="SECONDARY"
                    inline
                    href={savedSearchPageHref}
                    NextLink={NextLink}
                    underline
                  >
                    View all Saved Searches
                  </Link>
                </>
              ),
              closeOnClick: false,
            });
          } else
            fireToast({
              type: 'ERROR',
              text: 'Oops! Something went wrong, please try again later',
            });
        }
      } else {
        fireToast({
          type: 'ERROR',
          text: 'Oops! Something went wrong, please try again later',
        });
      }
    } else if (status === 'unauthenticated') {
      setSessionStorageItem('signin', modelSessionStorageItem);
      handleLogin();
    }
  };

  useOnUpdateOnly(() => {
    if (!userId) {
      setIsSearchSaved(false);
    }
  }, [userId]);

  useOnUpdateOnly(() => {
    setIsSearchSaved(false);
  }, [adId]);

  useEffect(() => {
    if (getSessionStorageItem('signin') === modelSessionStorageItem) {
      onOpen();
      removeSessionStorageItem('signin');
    }
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isSearchSaved,
    onOpen,
  };
};

export { useSavedSearchPanel };
